import type {
  EarningsRange,
  GameHoldOutcomeFilter,
  LedgerReportRange,
} from "@pax/api-contract/accounting/v1alpha1";
import { EarningsRangeTabs, PresetEarnings } from "@/features/earnings/PresetEarnings";
import { Heading, Tabs, Workspace } from "@/features/evals/components";
import {
  CopyId,
  DEFAULT_PAGE_SIZE,
  type LedgerLinks,
  LedgerLinksContext,
  LedgerTimesContext,
  Note,
  type PageSize,
} from "./components";
import { GameView } from "./GameView";
import { PlayerView } from "./PlayerView";
import { PersonPicker, PresetIdFilter, PresetPicker } from "./pickers";
import { Journals, SystemFlows } from "./Reports";
import { SupportAdjustments } from "./SupportAdjustments";
import type { GraphSettings } from "./TimeGraph";

export const LEDGER_TABS = [
  "player",
  "game",
  "support",
  "presetCredits",
  "flows",
  "journals",
] as const;
export type LedgerTab = (typeof LEDGER_TABS)[number];

const tabLabels: Record<LedgerTab, string> = {
  flows: "System flows",
  journals: "Journals",
  presetCredits: "Preset earnings",
  player: "Players",
  game: "Game",
  support: "Support",
};

export interface LedgerLocation extends GraphSettings {
  /** Player when only `userId` is given, else system flows. */
  tab?: LedgerTab;
  range?: LedgerReportRange;
  /** How many ranges back the report tabs show; missing for the newest. */
  back?: number;
  accountCode?: string[];
  journal?: string[];
  /** System flows and journals signed by which way they moved player balances. */
  signed?: true;
  /** The creator whose preset earnings to show, or the player whose support adjustments to show. */
  recipientUserId?: string;
  /** The staff member whose support adjustments to show. */
  granterUserId?: string;
  presetId?: string;
  earningsRange?: EarningsRange;
  /** How many earnings ranges back preset earnings show; missing for the newest. */
  earningsBack?: number;
  /** The presets or recipients the preset earnings chart shows: preset ids, `other`, or user ids. */
  earningsPart?: string[];
  userId?: string;
  /**
   * Where the page starts, just older than this row: the player's history at an instant, a cut,
   * or both; the game's holds at a hold; support adjustments at an operation. Missing starts at
   * the newest.
   */
  start?: string;
  /** The game the game tab shows, or the one the player's games list finds. */
  gameId?: string;
  /** Rows to a page of the player's history or the game's holds. Support adjustments come 50 to a page. */
  pageSize?: PageSize;
  /** The player's history and the game's holds with every row on its own, nothing folded together. */
  expandAll?: true;
  /** Times read as how long ago rather than the date and time. */
  ago?: true;
  /** The game tab's holds: how they ended, and when they were placed. */
  outcome?: GameHoldOutcomeFilter;
  placedFrom?: string;
  placedTo?: string;
  /** The game tab's holds: only those this player held credits for. */
  payerUserId?: string;
}

export function LedgerWorkspace({
  location,
  navigate,
}: {
  location: LedgerLocation;
  /** `push` adds a Back step; `replace`, for filters, ranges, and tags, does not. */
  navigate: (next: LedgerLocation, history: "push" | "replace") => void;
}) {
  const update = (patch: Partial<LedgerLocation>) => navigate({ ...location, ...patch }, "replace");
  const go = (patch: Partial<LedgerLocation>) => navigate({ ...location, ...patch }, "push");
  const tab = location.tab ?? "player";
  const range = location.range ?? "24h";
  const back = location.back ?? 0;
  /** A step to another period is worth a Back step; anything else replaces the address. */
  const reportChange = (patch: Partial<LedgerLocation>) =>
    "back" in patch && !("range" in patch) ? go(patch) : update(patch);
  const graph = {
    graph: location,
    onGraphChange: (patch: Partial<GraphSettings>) =>
      "graphBack" in patch && !("graphRange" in patch) ? go(patch) : update(patch),
  };
  const pages = {
    start: location.start,
    onStartChange: (start: string | undefined) => go({ start }),
    pageSize: location.pageSize ?? DEFAULT_PAGE_SIZE,
    onPageSizeChange: (pageSize: PageSize) =>
      update({ pageSize: pageSize === DEFAULT_PAGE_SIZE ? undefined : pageSize }),
  };
  const openGame = (patch: Partial<LedgerLocation>) =>
    navigate(
      {
        tab: "game",
        pageSize: location.pageSize,
        expandAll: location.expandAll,
        ago: location.ago,
        ...patch,
      },
      "push",
    );
  const links: LedgerLinks = {
    openPlayer: userId =>
      navigate(
        {
          tab: "player",
          userId,
          pageSize: location.pageSize,
          expandAll: location.expandAll,
          ago: location.ago,
        },
        "push",
      ),
    openGame: gameId => openGame({ gameId }),
  };
  const times = {
    ago: location.ago === true,
    toggle: () => update({ ago: location.ago ? undefined : true }),
  };
  return (
    <LedgerLinksContext value={links}>
      <LedgerTimesContext value={times}>
        <Workspace>
          <Heading title="Ledger" />
          <Tabs
            value={tab}
            options={LEDGER_TABS.map(tab => ({ value: tab, label: tabLabels[tab] }))}
            onChange={tab =>
              navigate(
                {
                  tab,
                  range: location.range,
                  back: location.back,
                  signed: location.signed,
                  ago: location.ago,
                },
                "push",
              )
            }
          />
          {tab === "flows" && (
            <SystemFlows
              range={range}
              back={back}
              accountCodes={location.accountCode}
              signed={location.signed === true}
              onChange={reportChange}
            />
          )}
          {tab === "journals" && (
            <Journals
              range={range}
              back={back}
              journals={location.journal}
              signed={location.signed === true}
              onChange={reportChange}
            />
          )}
          {tab === "presetCredits" && (
            <PresetEarningsTab location={location} onChange={update} onPick={go} />
          )}
          {tab === "player" && (
            <>
              <div className="-ml-3 flex flex-wrap items-center gap-1">
                <PersonPicker
                  label="Player"
                  anyone="No player"
                  userId={location.userId}
                  onChange={userId => go({ userId, start: undefined, gameId: undefined })}
                />
                {location.userId && <CopyId id={location.userId} label="user id" />}
              </div>
              {location.userId ? (
                <PlayerView
                  key={location.userId}
                  userId={location.userId}
                  gameId={location.gameId}
                  onGameIdChange={gameId => update({ gameId })}
                  onShowGameHolds={gameId => openGame({ gameId, payerUserId: location.userId })}
                  {...pages}
                  {...graph}
                  expandAll={location.expandAll === true}
                  onExpandAllChange={expandAll => update({ expandAll: expandAll || undefined })}
                />
              ) : (
                <Note>Pick a player to see their balances and history.</Note>
              )}
            </>
          )}
          {tab === "game" && (
            <GameView
              gameId={location.gameId}
              onGameChange={gameId => update({ gameId, start: undefined })}
              filter={{
                outcome: location.outcome,
                from: location.placedFrom,
                to: location.placedTo,
                payerUserId: location.payerUserId,
              }}
              onFilterChange={filter =>
                update({
                  outcome: filter.outcome,
                  placedFrom: filter.from,
                  placedTo: filter.to,
                  payerUserId: filter.payerUserId,
                  start: undefined,
                })
              }
              {...pages}
              {...graph}
              expandAll={location.expandAll === true}
              onExpandAllChange={expandAll => update({ expandAll: expandAll || undefined })}
            />
          )}
          {tab === "support" && (
            <SupportAdjustments
              filter={{
                granterUserId: location.granterUserId,
                recipientUserId: location.recipientUserId,
              }}
              onFilterChange={filter => update({ ...filter, start: undefined })}
              start={pages.start}
              onStartChange={pages.onStartChange}
            />
          )}
        </Workspace>
      </LedgerTimesContext>
    </LedgerLinksContext>
  );
}

/** Blank until a creator or preset is picked, so the earnings routes never fall back to the caller. */
function PresetEarningsTab({
  location,
  onChange,
  onPick,
}: {
  location: LedgerLocation;
  onChange: (patch: Partial<LedgerLocation>) => void;
  /** For a new creator or preset, which is worth a Back step. */
  onPick: (patch: Partial<LedgerLocation>) => void;
}) {
  const { recipientUserId, presetId } = location;
  const range = location.earningsRange ?? "30d";
  const back = location.earningsBack ?? 0;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <EarningsRangeTabs
          range={range}
          onChange={earningsRange => onChange({ earningsRange, earningsBack: undefined })}
        />
        <div className="-ml-1 flex flex-wrap gap-1">
          <PersonPicker
            label="Creator"
            anyone="Any creator"
            userId={recipientUserId}
            onChange={userId => onPick({ recipientUserId: userId, earningsPart: undefined })}
          />
          <PresetPicker
            presetId={presetId}
            onChange={id => onPick({ presetId: id, earningsPart: undefined })}
          />
        </div>
        <PresetIdFilter
          presetId={presetId}
          onChange={id => onPick({ presetId: id, earningsPart: undefined })}
        />
      </div>
      {recipientUserId || presetId ? (
        <PresetEarnings
          filter={{ recipientUserId, presetId }}
          range={range}
          back={back}
          onBackChange={earningsBack => onPick({ earningsBack: earningsBack || undefined })}
          parts={location.earningsPart ?? []}
          onPartsChange={earningsPart => onChange({ earningsPart })}
          recipientIds
          counted
        />
      ) : (
        <Note>Pick a creator or preset.</Note>
      )}
    </div>
  );
}
